/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020,2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * This manager is responsible for registering and handing drop events in the crosstab.
 */
define([
	'underscore',
	'../../../dataSources/utils/ShapingConstants'
], (
	_,
	ShapingConstants
) => {

	const GRID_ROW_EDGE = 'row';
	const GRID_COLUMN_EDGE = 'column';
	const GRID_VALUES_EDGE = 'values';

	return class GridDnDManager {

		constructor(options = {}) {
			this.grid = options.grid;
			this.dashboardApi = options.dashboardApi;
			this.content = options.content;
			this.gridEdgeMappedToId = options.gridEdgeMappedToId;
			this.visualization = this.content.getFeature('Visualization');
			this.visDnDUtils = this.content.getFeature('VisDnD.utils');
		}

		/**
		 *
		 * @param {React Component} component
		 */
		registerElement(component, domNode) {
			const DndManager = this.dashboardApi.getFeature('DashboardDnd.internal');
			DndManager.addDropTarget(domNode, {
				accepts: this.accepts.bind(this, component, domNode),
				onDrop: this.onDrop.bind(this, component),
				onDragEnter: this.onDragEnter.bind(this),
				onDragMove: this.onDragMove.bind(this, component),
				onDragLeave: this.onDragLeave.bind(this, component)
			});
		}

		deregisterElement(domNode) {
			const DndManager = this.dashboardApi.getFeature('DashboardDnd.internal');
			DndManager.removeDropTarget(domNode);
		}

		//DND callbacks
		accepts(component, targetNode, dragObject) {
			if (!component || !component.props) {
				return false;
			}

			let acceptsOptions = { dropTarget: ShapingConstants.DROP_TARGET_OPTIONS.SLOT };
			if (targetNode) {
				acceptsOptions.targetNode = targetNode;
			}

			const content = this._getContent();
			const visualization = content.getFeature('Visualization');
			let dropAction;
			if (visualization.getType() === 'Crosstab') {
				dropAction = targetNode && this._getDropAction(component, dragObject, targetNode);
				if (dropAction) {
					dropAction.actionSpec.slot = this._findSlot(dropAction.actionSpec.gridEdge);
					acceptsOptions.dropAction = dropAction;
				}
			}
			let accept = this.visDnDUtils.accepts(dragObject, acceptsOptions);

			// check if the current activate dropZone is visible, if not visible, we are not going to accept it
			if(accept) {
				const gridDom = this.grid.$el[0];
				const gridDomRect = gridDom && gridDom.getBoundingClientRect();
				const componentDom = component.ref.current;
				const componentDomRect = componentDom && componentDom.getBoundingClientRect();
				// allow tolerance for 3px regarding to vertical scroll bar and the padding on the right
				const tolerance = 3;
				if (!gridDomRect || !componentDomRect || parseInt(gridDomRect.bottom) < parseInt(componentDomRect.bottom) || (parseInt(gridDomRect.right) < parseInt(componentDomRect.right) && Math.abs(componentDomRect.right - gridDomRect.right) > tolerance)){
					accept = false;
				}
			}

			return accept;
		}

		onDragEnter() {}

		_findSlot(gridEdge) {
			const slots = this.visualization.getSlots();
			const slot = _.find(slots.getSlotList(), slot => {
				switch ( gridEdge ) {
					case GRID_ROW_EDGE:
						return slot.getId() === this.gridEdgeMappedToId.row;
					case GRID_COLUMN_EDGE:
						return slot.getId() === this.gridEdgeMappedToId.column;
					case GRID_VALUES_EDGE:
						return slot.getId() === this.gridEdgeMappedToId.values;
				}
			});
			return slot;
		}

		_insertDataItems({ gridEdge, droppedColumns, position }) {
			const slot = this._findSlot(gridEdge);
			if ( slot ) {
				this.visDnDUtils.mapColumns(slot.getId(), droppedColumns, { position: position, bReplace: false });
			}
		}

		_replaceDataItems({ gridEdge, droppedColumns, position }) {
			const slot = this._findSlot(gridEdge);
			if ( slot ) {
				const slotDataItemList = slot.getDataItemList();
				if (_.isEmpty(slotDataItemList)) {
					return;
				}

				this.visDnDUtils.mapColumns(slot.getId(), droppedColumns, { position, bReplace: true });
			}
		}

		_findValuesSlotPosition(component) {
			const slot = this._findSlot(GRID_VALUES_EDGE);
			let index = -1;
			if ( slot ) {
				const dataItemList = slot.getDataItemList();
				if ( !_.isEmpty(dataItemList) ) {
					index = dataItemList.findIndex( (dataItem) => {
						return ( dataItem.getLabel() === component.props.useValue );
					});
				}
			}
			return index;
		}

		_getDropAction(component, dragObject, node) {
			const droppedColumns =  dragObject.data.columns;
			const metadataColumns = _.pluck(droppedColumns, 'metadataColumn');

			if (metadataColumns.length > 0) {
				const { action, idx } = this._getDropZoneInfo(dragObject, node, component);

				switch ( action ) {
					case 'insertLeft': return this._getInsertLeftAction(component, droppedColumns, idx);
					case 'insertRight': return this._getInsertRightAction(component, droppedColumns, idx);
					case 'replaceRow': return this._getReplaceRowAction(component, droppedColumns, idx);
					case 'insertAbove': return this._getInsertAboveAction(droppedColumns, idx);
					case 'insertBelow': return this._getInsertBelowAction(droppedColumns, idx);
					case 'replaceCol': return this._getReplaceColumnAction(component, droppedColumns, idx);
				}
			}
		}

		onDrop(component, dragObject, node) {
			const dropAction = this._getDropAction(component, dragObject, node);
			this.grid.hideDropZones();
			return dropAction.action(dropAction.actionSpec);
		}

		onDragMove(component, dragObject, node) {
			const { action, idx } = this._getDropZoneInfo(dragObject, node, component);
			switch ( action ) {
				case 'insertLeft': return this.grid.showLeftDropZone(idx);
				case 'insertRight': return this.grid.showRightDropZone(idx);
				case 'replaceRow': {
					if ( component.props.isMeasure && component.props.cellType !== 'corner') {
						return this.grid.showReplaceDropZone(component.props.row, 'row', component.props.useValue);
					} else {
						return this.grid.showReplaceDropZone(idx, 'col');
					}
				}
				case 'insertAbove': return this.grid.showTopDropZone(idx);
				case 'insertBelow': return this.grid.showBottomDropZone(idx);
				case 'replaceCol': return this.grid.showReplaceDropZone(idx, 'row');
			}
		}

		onDragLeave() {
			this.grid.hideDropZones();
		}

		_getInsertLeftAction(component, droppedColumns, position) {
			let actionSpec;
			if ( component.props.cellType === 'corner' ) {
				actionSpec = {
					gridEdge: GRID_VALUES_EDGE,
					droppedColumns: droppedColumns,
					position: 0
				};
			} else if ( component.props.isMeasure ) {
				actionSpec = {
					gridEdge: GRID_VALUES_EDGE,
					droppedColumns: droppedColumns,
					position: this._findValuesSlotPosition(component)
				};
			} else {
				actionSpec = {
					gridEdge: GRID_ROW_EDGE,
					droppedColumns: droppedColumns,
					position: parseInt(position)
				};
			}
			actionSpec.replace = false;
			return {
				actionSpec: actionSpec,
				action: this._insertDataItems.bind(this)
			};
		}

		_getInsertRightAction(component, droppedColumns, position) {
			let actionSpec;
			if ( component.props.cellType === 'corner' ) {
				actionSpec = {
					gridEdge: GRID_VALUES_EDGE,
					droppedColumns: droppedColumns,
					position: -1
				};
			} else if ( component.props.isMeasure ) {
				actionSpec = {
					gridEdge: GRID_VALUES_EDGE,
					droppedColumns: droppedColumns,
					position: this._findValuesSlotPosition(component) + 1
				};
			}  else {
				actionSpec = {
					gridEdge: GRID_ROW_EDGE,
					droppedColumns: droppedColumns,
					position: parseInt(position)+1
				};
			}
			actionSpec.replace = false;
			return {
				actionSpec: actionSpec,
				action: this._insertDataItems.bind(this)
			};
		}

		_getReplaceRowAction(component, droppedColumns, position) {
			let actionSpec;
			if ( component.props.cellType === 'corner' ) {
				actionSpec = {
					gridEdge: GRID_VALUES_EDGE,
					droppedColumns: droppedColumns,
					position: 0
				};
			} else if ( component.props.isMeasure ) {
				actionSpec = {
					gridEdge: GRID_VALUES_EDGE,
					droppedColumns: droppedColumns,
					position: this._findValuesSlotPosition(component)
				};
			} else {
				actionSpec = {
					gridEdge: GRID_ROW_EDGE,
					droppedColumns: droppedColumns,
					position: parseInt(position)
				};
			}
			actionSpec.replace = true;
			return {
				actionSpec: actionSpec,
				action: this._replaceDataItems.bind(this)
			};
		}

		_getInsertAboveAction(droppedColumns, position) {
			const actionSpec = {
				gridEdge: GRID_COLUMN_EDGE,
				droppedColumns: droppedColumns,
				position: parseInt(position),
				replace: false
			};
			return {
				actionSpec: actionSpec,
				action: this._insertDataItems.bind(this)
			};
		}

		_getInsertBelowAction(droppedColumns, position) {
			const actionSpec = {
				gridEdge: GRID_COLUMN_EDGE,
				droppedColumns: droppedColumns,
				position: parseInt(position)+1
			};
			actionSpec.replace = false;
			return {
				actionSpec: actionSpec,
				action: this._insertDataItems.bind(this)
			};
		}

		_getReplaceColumnAction(component, droppedColumns, position) {
			let actionSpec;
			if ( component.props.isMeasure ) {
				actionSpec = {
					gridEdge: GRID_VALUES_EDGE,
					droppedColumns: droppedColumns,
					position: this._findValuesSlotPosition(component)
				};
			}  else {
				actionSpec = {
					gridEdge: GRID_COLUMN_EDGE,
					droppedColumns: droppedColumns,
					position: parseInt(position)
				};
			}
			actionSpec.replace = true;
			return {
				actionSpec: actionSpec,
				action: this._replaceDataItems.bind(this)
			};
		}

		_getDropZoneInfo(dragObject, node, component) {
			if ( component.props.cellType === 'column' ) {
				return this._getColumnDropZoneInfo(dragObject, node, component);
			} else if ( component.props.cellType === 'row' ) {
				return this._getRowDropZoneInfo(dragObject, node, component);
			} else if ( component.props.cellType === 'corner' ) {
				return this._getCornerDropZoneInfo(dragObject, node, component);
			}
		}

		_getColumnDropZoneInfo(dragObject, node) {
			const clientRect = node.getBoundingClientRect();
			const col = node && node.getAttribute('col');
			const diff = dragObject.position.x - clientRect.left;
			const percent = 100 * (diff / clientRect.width);
			if ( percent < 33 ) {
				return { action: 'insertLeft', idx: col };
			} else if (percent > 66) {
				return { action: 'insertRight', idx: col };
			} else {
				return { action: 'replaceRow', idx: col };
			}
		}

		_getRowDropZoneInfo(dragObject, node, component) {
			const clientRect = node.getBoundingClientRect();
			if ( component.props.isMeasure ) {
				return this._getColumnDropZoneInfo(dragObject, node);
			} else {
				const row = node && node.getAttribute('row');
				const diff = dragObject.position.y - clientRect.top;
				const percent = 100 * (diff / clientRect.height);
				if ( percent < 33 ) {
					return { action: 'insertAbove', idx: row };
				} else if ( percent > 66 ) {
					return { action: 'insertBelow', idx: row };
				} else {
					return { action: 'replaceCol', idx: row };
				}
			}

		}

		_getCornerDropZoneInfo(dragObject, node) {
			const clientRect = node.getBoundingClientRect();
			const diff = dragObject.position.x - clientRect.left;
			const percent = 100 * (diff / clientRect.width);
			if ( percent < 33 ) {
				return { action: 'insertLeft', idx: 'corner' };
			} else if (percent > 66) {
				return { action: 'insertRight', idx: 'corner' };
			} else {
				return { action: 'replaceRow', idx: 'corner' };
			}
		}

		_getContent () {
			if (!this.content) {
				const view = this.grid.gridView || this.grid.crosstabView;
				this.content = this.dashboardApi.getCanvas() && this.dashboardApi.getCanvas().getContent(view.ownerWidget.getId());
			}
			return this.content;
		}

	};
});
